import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserCampaign, checkCampaignJoined, firstTimeLogin } from "../../../api/api";
import { verifyUserMobileLogin } from "../../../api/api";
import Cookies from "js-cookie";
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Fade,
    Button,
    Alert,
} from "@mui/material";
import { CheckCircle as CheckIcon, Error as ErrorIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const BackgroundBox = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
}));

const LoadingCard = styled(Box)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 24,
    padding: '48px 64px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    maxWidth: 500,
    width: '90%',
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    animation: 'scaleIn 0.5s ease-out',
    '@keyframes scaleIn': {
        '0%': {
            transform: 'scale(0)',
            opacity: 0,
        },
        '100%': {
            transform: 'scale(1)',
            opacity: 1,
        },
    },
}));

const ErrorIconBox = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    animation: 'scaleIn 0.5s ease-out',
}));

const SpinnerBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'inline-block',
    marginBottom: 24,
}));

const VerifyMobileRegister = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const [errorDetails, setErrorDetails] = useState('');
    const [nextSteps, setNextSteps] = useState([]);

    useEffect(() => {
        const verifyLogin = async () => {
            const queryParams = new URLSearchParams(window.location.search);
            const user = queryParams.get("user");
            let campaignCode = queryParams.get("campaignCode");
            
            if (campaignCode) {
                campaignCode = campaignCode.split("?")[0];
            }
            
            if (!campaignCode) {
                setStatus('error');
                setMessage('Kampanjekode mangler');
                setErrorDetails('Vi kunne ikke finne kampanjekoden i lenken. Dette kan skje hvis lenken er ugyldig eller utløpt.');
                setNextSteps([
                    'Kontroller at lenken er komplett',
                    'Prøv å klikke på lenken på nytt',
                    'Kontakt support hvis problemet vedvarer'
                ]);
                return;
            }

            if (!user) {
                setStatus('error');
                setMessage('Ugyldig innlogging');
                setErrorDetails('Vi kunne ikke verifisere din identitet. Dette kan skje hvis innloggingsprosessen ble avbrutt.');
                setNextSteps([
                    'Gå tilbake til innloggingssiden',
                    'Prøv å logge inn på nytt',
                    'Kontakt support hvis problemet vedvarer'
                ]);
                return;
            }

            try {
                const { data } = await verifyUserMobileLogin(user);

                // Log for debugging
                console.log("verifyUserMobileLogin response:", data);

                if (data.status === true) {
                    // Existing user trying to join campaign
                    try {
                        if (campaignCode) {
                            setStatus('loading');
                            setMessage('Sjekker kampanjestatus...');
                            
                            const joinedRes = await checkCampaignJoined({
                                user_id: data.user.id,
                                campaign_code: campaignCode,
                            });

                            if (joinedRes.data.joined) {
                                setStatus('error');
                                setMessage('Du har allerede deltatt');
                                setErrorDetails('Du har allerede deltatt i denne kampanjen. Du kan se dine kuponger ved å logge inn.');
                                setNextSteps([
                                    'Gå til innloggingssiden',
                                    'Logg inn med ditt telefonnummer',
                                    'Se dine eksisterende kuponger'
                                ]);
                                return;
                            }

                            setStatus('loading');
                            setMessage('Legger til kampanje...');
                            
                            Cookies.set("user_id", data.user.id, { 
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });
                            
                            const campaignRes = await createUserCampaign({
                                user_id: data.user.id,
                                campaign_code: campaignCode,
                                user: user,
                            });
                            
                            if (campaignRes.data.company_id) {
                                Cookies.set("company_id", campaignRes.data.company_id, { 
                                    expires: 90,
                                    path: '/',
                                    sameSite: 'lax'
                                });
                            }
                            
                            // Check if this is a first-time user (needs acceptance)
                            try {
                                const firsttimeCheck = await firstTimeLogin({
                                    user: user,
                                });
                                
                                const isFirstTime = firsttimeCheck?.data?.first_time === true;
                                
                                // Verify cookies are set
                                const userId = Cookies.get("user_id");
                                const compId = Cookies.get("company_id");
                                if (!userId || !compId) {
                                    // Retry setting cookies
                                    Cookies.set("user_id", data.user.id, { 
                                        expires: 90,
                                        path: '/',
                                        sameSite: 'lax'
                                    });
                                    if (campaignRes.data.company_id) {
                                        Cookies.set("company_id", campaignRes.data.company_id, { 
                                            expires: 90,
                                            path: '/',
                                            sameSite: 'lax'
                                        });
                                    }
                                }

                                setStatus('success');
                                setMessage('Kampanje lagt til!');
                                setTimeout(() => {
                                    if (isFirstTime) {
                                        // First-time user needs to accept terms
                                        navigate("/user_acceptance", {
                                            state: { 
                                                mobile: user,
                                                company_id: campaignRes.data.company_id,
                                                campaign_code: campaignCode
                                            }
                                        });
                                    } else {
                                        // Existing user who already accepted before
                                        navigate("/user_home", {
                                            state: { company_id: campaignRes.data.company_id }
                                        });
                                    }
                                }, 1500);
                            } catch (firstTimeErr) {
                                // If check fails, default to acceptance page to be safe
                                console.error("Error checking first-time status:", firstTimeErr);
                                setStatus('success');
                                setMessage('Kampanje lagt til!');
                                setTimeout(() => {
                                    navigate("/user_acceptance", {
                                        state: { 
                                            mobile: user,
                                            company_id: campaignRes.data.company_id,
                                            campaign_code: campaignCode
                                        }
                                    });
                                }, 1500);
                            }
                            return;
                        } else {
                            Cookies.set("user_id", data.user.id, { expires: 90 });
                        }
                    } catch (trackErr) {
                        const errorMsg = trackErr.response?.data?.message || trackErr.response?.data?.error || "";
                        
                        if (trackErr.response?.status === 403 || 
                            errorMsg.includes('already') || 
                            errorMsg.includes('joined') || 
                            errorMsg.includes('deltatt')) {
                            setStatus('error');
                            setMessage('Du har allerede deltatt');
                            setErrorDetails('Du har allerede deltatt i denne kampanjen. Logg inn for å se dine kuponger.');
                            setNextSteps([
                                'Gå til innloggingssiden',
                                'Logg inn med ditt telefonnummer'
                            ]);
                        } else if (errorMsg.includes('expired') || errorMsg.includes('utløpt')) {
                            setStatus('error');
                            setMessage('Kampanjen har utløpt');
                            setErrorDetails('Denne kampanjen er ikke lenger tilgjengelig. Kampanjer har en begrenset varighet.');
                            setNextSteps([
                                'Sjekk om det finnes andre aktive kampanjer',
                                'Kontakt oss for mer informasjon'
                            ]);
                        } else if (errorMsg.includes('limit') || errorMsg.includes('grense')) {
                            setStatus('error');
                            setMessage('Kampanjen er full');
                            setErrorDetails('Denne kampanjen har nådd maksimalt antall deltakere.');
                            setNextSteps([
                                'Prøv en annen kampanje',
                                'Kontakt oss for mer informasjon'
                            ]);
                        } else {
                            setStatus('error');
                            setMessage('Kunne ikke delta i kampanje');
                            setErrorDetails(errorMsg || 'En uventet feil oppstod ved tilgang til kampanjen.');
                            setNextSteps([
                                'Prøv å oppdatere siden',
                                'Kontroller internettforbindelsen din',
                                'Kontakt support hvis problemet vedvarer'
                            ]);
                        }
                        return;
                    }
                } else {
                    // New user registration - this is expected for new users
                    // The API returns status: false with message "User not found. Please register."
                    // This is normal and we proceed to create the user via campaign registration
                    console.log("New user detected, proceeding with campaign registration");
                    
                    if (campaignCode) {
                        try {
                            setStatus('loading');
                            setMessage('Registrerer deg...');
                            
                            const res = await createUserCampaign({
                                user_id: 0,
                                campaign_code: campaignCode,
                                user: user,
                            });

                            const { user_id, company_id } = res.data;

                            // Set cookies with proper settings
                            Cookies.set("user_id", user_id, {
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });
                            Cookies.set("company_id", company_id, {
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });

                            // Verify cookies are set before navigating
                            const verifyCookies = () => {
                                const userId = Cookies.get("user_id");
                                const compId = Cookies.get("company_id");
                                if (!userId || !compId) {
                                    console.warn("Cookies not set, retrying...");
                                    // Retry setting cookies
                                    Cookies.set("user_id", user_id, {
                                        expires: 90,
                                        path: '/',
                                        sameSite: 'lax'
                                    });
                                    Cookies.set("company_id", company_id, {
                                        expires: 90,
                                        path: '/',
                                        sameSite: 'lax'
                                    });
                                }
                            };

                            setStatus('success');
                            setMessage('Registrering vellykket!');
                            setTimeout(() => {
                                verifyCookies();
                                navigate("/user_acceptance", {
                                    state: { 
                                        mobile: user,
                                        company_id,
                                        campaign_code: campaignCode
                                    },
                                });
                            }, 1500);
                        } catch (campaignErr) {
                            const errorMsg = campaignErr.response?.data?.message || campaignErr.response?.data?.error || "";
                            
                            if (errorMsg.includes('already') || errorMsg.includes('joined') || errorMsg.includes('deltatt')) {
                                setStatus('error');
                                setMessage('Du har allerede deltatt');
                                setErrorDetails('Du har allerede deltatt i denne kampanjen. Logg inn for å se dine kuponger.');
                                setNextSteps([
                                    'Gå til innloggingssiden',
                                    'Logg inn med ditt telefonnummer'
                                ]);
                            } else if (errorMsg.includes('expired') || errorMsg.includes('utløpt')) {
                                setStatus('error');
                                setMessage('Kampanjen har utløpt');
                                setErrorDetails('Denne kampanjen er ikke lenger tilgjengelig.');
                                setNextSteps([
                                    'Sjekk om det finnes andre aktive kampanjer',
                                    'Kontakt oss for mer informasjon'
                                ]);
                            } else {
                                setStatus('error');
                                setMessage('Registrering mislyktes');
                                setErrorDetails(errorMsg || 'En feil oppstod ved registrering. Vennligst prøv igjen.');
                                setNextSteps([
                                    'Prøv å oppdatere siden',
                                    'Kontroller internettforbindelsen din',
                                    'Kontakt support hvis problemet vedvarer'
                                ]);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error in verifyUserMobileLogin:", error);
                
                // Check if this is actually a "user not found" response (which is OK for new users)
                const responseData = error.response?.data;
                if (responseData && responseData.status === false && responseData.message?.includes('not found')) {
                    // This is expected for new users - proceed with registration
                    console.log("User not found (expected for new users), proceeding with campaign registration");
                    
                    if (campaignCode) {
                        try {
                            setStatus('loading');
                            setMessage('Registrerer deg...');
                            
                            const res = await createUserCampaign({
                                user_id: 0,
                                campaign_code: campaignCode,
                                user: user,
                            });

                            const { user_id, company_id } = res.data;

                            Cookies.set("user_id", user_id, {
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });
                            Cookies.set("company_id", company_id, {
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });

                            const verifyCookies = () => {
                                const userId = Cookies.get("user_id");
                                const compId = Cookies.get("company_id");
                                if (!userId || !compId) {
                                    console.warn("Cookies not set, retrying...");
                                    Cookies.set("user_id", user_id, {
                                        expires: 90,
                                        path: '/',
                                        sameSite: 'lax'
                                    });
                                    Cookies.set("company_id", company_id, {
                                        expires: 90,
                                        path: '/',
                                        sameSite: 'lax'
                                    });
                                }
                            };

                            setStatus('success');
                            setMessage('Registrering vellykket!');
                            setTimeout(() => {
                                verifyCookies();
                                navigate("/user_acceptance", {
                                    state: { 
                                        mobile: user,
                                        company_id,
                                        campaign_code: campaignCode
                                    },
                                });
                            }, 1500);
                        } catch (campaignErr) {
                            const errorMsg = campaignErr.response?.data?.message || campaignErr.response?.data?.error || "";
                            
                            if (errorMsg.includes('already') || errorMsg.includes('joined') || errorMsg.includes('deltatt')) {
                                setStatus('error');
                                setMessage('Du har allerede deltatt');
                                setErrorDetails('Du har allerede deltatt i denne kampanjen. Logg inn for å se dine kuponger.');
                                setNextSteps([
                                    'Gå til innloggingssiden',
                                    'Logg inn med ditt telefonnummer'
                                ]);
                            } else if (errorMsg.includes('expired') || errorMsg.includes('utløpt')) {
                                setStatus('error');
                                setMessage('Kampanjen har utløpt');
                                setErrorDetails('Denne kampanjen er ikke lenger tilgjengelig.');
                                setNextSteps([
                                    'Sjekk om det finnes andre aktive kampanjer',
                                    'Kontakt oss for mer informasjon'
                                ]);
                            } else {
                                setStatus('error');
                                setMessage('Registrering mislyktes');
                                setErrorDetails(errorMsg || 'En feil oppstod ved registrering. Vennligst prøv igjen.');
                                setNextSteps([
                                    'Prøv å oppdatere siden',
                                    'Kontroller internettforbindelsen din',
                                    'Kontakt support hvis problemet vedvarer'
                                ]);
                            }
                        }
                    }
                    return;
                }
                
                const errorMsg = error.response?.data?.message || error.response?.data?.error || "";
                
                if (errorMsg.includes('already') || errorMsg.includes('joined') || errorMsg.includes('deltatt')) {
                    setStatus('error');
                    setMessage('Du har allerede deltatt');
                    setErrorDetails('Du har allerede deltatt i denne kampanjen. Logg inn for å se dine kuponger.');
                    setNextSteps([
                        'Gå til innloggingssiden',
                        'Logg inn med ditt telefonnummer'
                    ]);
                } else if (error.response?.status === 404) {
                    setStatus('error');
                    setMessage('Kampanje ikke funnet');
                    setErrorDetails('Kampanjekoden er ikke gyldig eller kampanjen eksisterer ikke lenger.');
                    setNextSteps([
                        'Kontroller at kampanjekoden er riktig',
                        'Kontakt oss hvis du mener dette er en feil'
                    ]);
                } else {
                    setStatus('error');
                    setMessage('Verifisering mislyktes');
                    setErrorDetails('Vi kunne ikke verifisere din innlogging. Dette kan skyldes nettverksproblemer eller serverfeil.');
                    setNextSteps([
                        'Sjekk internettforbindelsen din',
                        'Prøv å oppdatere siden',
                        'Kontakt support hvis problemet vedvarer'
                    ]);
                }
            }
        };

        verifyLogin();
    }, [navigate]);

    const handleRetry = () => {
        window.location.reload();
    };

    const handleGoToLogin = () => {
        navigate("/");
    };

    return (
            <BackgroundBox>
                <Container maxWidth="sm">
                    <Fade in={true} timeout={500}>
                        <LoadingCard>
                            {status === 'loading' && (
                                <>
                                    <SpinnerBox>
                                        <CircularProgress 
                                            size={80} 
                                            thickness={4}
                                            sx={{
                                                color: 'primary.main',
                                            }}
                                        />
                                    </SpinnerBox>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                        Verifiserer registrering...
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        {message || 'Vennligst vent mens vi registrerer deg og legger til kampanjen'}
                                    </Typography>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <SuccessIcon>
                                        <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
                                    </SuccessIcon>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                        {message}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                                        Du blir nå videresendt...
                                    </Typography>
                                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <ErrorIconBox>
                                        <ErrorIcon sx={{ fontSize: 48, color: 'white' }} />
                                    </ErrorIconBox>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'error.main', mb: 2 }}>
                                        {message}
                                    </Typography>
                                    
                                    {errorDetails && (
                                        <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                                            {errorDetails}
                                        </Alert>
                                    )}

                                    {nextSteps.length > 0 && (
                                        <Box sx={{ mb: 3, textAlign: 'left' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                                                Hva kan du gjøre?
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                                {nextSteps.map((step, index) => (
                                                    <Typography 
                                                        key={index}
                                                        component="li" 
                                                        variant="body2" 
                                                        sx={{ mb: 0.5, color: 'text.secondary' }}
                                                    >
                                                        {step}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleRetry}
                                            sx={{ minWidth: 120 }}
                                        >
                                            Prøv igjen
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleGoToLogin}
                                            sx={{ minWidth: 120 }}
                                        >
                                            Gå til innlogging
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </LoadingCard>
                    </Fade>
                </Container>
            </BackgroundBox>
    );
};

export default VerifyMobileRegister;
